import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import EnquiryForm from './Landing/EnquiryForm';
import { landingAPI, resolveImage } from '../utils/api';
import Hero from './Landing/Hero';
import About from './Landing/About';
import Pricing from './Landing/Pricing';
import Classes from './Landing/Classes';
import MerchandiseSection from './Landing/MerchandiseSection';
import CTA from './Landing/CTA';
import { tField } from '../utils/bilingualHelper';

/* ─── DATA ─────────────────────────────────────────── */
const FEATURES = [
  { emoji:'📊',title:{en:'Attendance Tracking',zh:'出勤記錄'},desc:{en:'Real-time Present / Absent / Late marking with monthly percentage graphs.',zh:'即時出席/缺席/遲到記錄，附有每月百分比圖表。'} },
  { emoji:'🎯',title:{en:'Personal Objectives',zh:'個人目標'},desc:{en:'Set skill goals and watch progress bars move from 0 to 100% session by session.',zh:'設定技巧目標，並見證進度條逐節課從 0 到 100% 成長。'} },
  { emoji:'⭐',title:{en:'Achievement Badges',zh:'成就徽章'},desc:{en:'Earn Star Performer, Perfect Attendance, and 8 more milestone badges.',zh:'獲得明星學員、全勤獎及其他 8 個里程碑徽章。'} },
  { emoji:'📝',title:{en:'Tutor Notes',zh:'導師筆記'},desc:{en:'Personalised, dated feedback from your tutor after every class.',zh:'每堂課後，導師為您提供個人化及有日期的意見反饋。'} },
  { emoji:'💳',title:{en:'Fee Management',zh:'學費管理'},desc:{en:'Paid, Pending, Overdue — always visible. Never miss a payment reminder.',zh:'已付、待付、逾期 — 一目了然。絕不錯過任何付款提醒。'} },
  { emoji:'📹',title:{en:'Video Library',zh:'影片庫'},desc:{en:'48+ curated dance videos and 22 theory documents, organised by category.',zh:'48+ 精選舞蹈影片和 22 份理論文檔，分類整理。'} },
  { emoji:'👨‍👩‍👧',title:{en:'Parent Portal',zh:'家長介面'},desc:{en:'Guardians track attendance, notes, and progress in real time.',zh:'監護人可即時追蹤出勤、筆記和學習進度。'} },
  { emoji:'🔔',title:{en:'Smart Reminders',zh:'智能提醒'},desc:{en:'Push notifications before every class so you never miss a beat.',zh:'每堂課前推送通知，讓您絕不錯過任何細節。'} },
];
const TESTIMONIALS = [
  { initial:'A',name:'Alex Doe',role:{en:'Hip Hop — Advanced',zh:'嘻哈 — 高級'},stars:5,quote:{en:'ETS completely changed how I track my dance journey. My attendance, tutor notes, and progress — all in one place. Like having a personal coach in my pocket.',zh:'ETS 完全改變了我記錄舞蹈旅程的方式。我的出勤、導師筆記和進度 — 全在一個地方。就像口袋裡有一位私人教練。'} },
  { initial:'M',name:'Tutor Mike',role:{en:'Salsa Instructor',zh:'薩爾薩舞導師'},stars:5,quote:{en:'Managing 61 students across 4 batches was a nightmare. ETS cut my admin time from 3 hours per week to under 20 minutes.',zh:'管理 4 個班級的 61 名學生曾是一場噩夢。ETS 將我每週的行政時間從 3 小時縮減到不到 20 分鐘。'} },
  { initial:'P',name:'Mrs. Patel',role:{en:'Parent — Guardian',zh:'家長 — 監護人'},stars:5,quote:{en:"Seeing my daughter's progress every week is incredible. Attendance, tutor notes, achievement badges — I feel fully involved.",zh:'每週看到我女兒的進步真是令人難以置信。出勤、導師筆記、成就徽章 — 讓我感覺完全參與其中。'} },
  { initial:'S',name:'Sarah L.',role:{en:'Ballet — Beginner',zh:'芭蕾 — 初級'},stars:4,quote:{en:'I love how easy it is to see the video library right from my dashboard. It helps me practice my form at home perfectly!',zh:'我喜歡從我的儀表板輕鬆查看影片庫。它幫助我在家裡完美地練習姿勢！'} },
  { initial:'J',name:'James Chen',role:{en:'Breakdance',zh:'霹靂舞'},stars:5,quote:{en:'The objective tracking is a game changer. Seeing my progress bar hit 100% on the windmill move was the best feeling ever.',zh:'目標追蹤改變了遊戲規則。看到我在風車動作的進度條達到 100% 是有史以來最棒的感覺。'} },
  { initial:'E',name:'Elena V.',role:{en:'Contemporary Tutor',zh:'當代舞導師'},stars:5,quote:{en:'The automated fee reminders mean I never have to awkwardly ask parents for payments anymore. It is all handled beautifully.',zh:'自動學費提醒意味著我再也不用尷尬地向家長要求付款了。一切都處理得非常完美。'} }
];
const STEPS = [
  { emoji:'📱',step:'01',title:{en:'Sign Up Free',zh:'免費註冊'},desc:{en:'Register your account using your email and mobile number. Secure credentials, no hassle.',zh:'使用您的電子郵件和手機號碼註冊帳戶。安全認證，毫無煩惱。'} },
  { emoji:'🎯',step:'02',title:{en:'Choose Your Style',zh:'選擇您的風格'},desc:{en:'Pick your dance form and preferred skill level. We have something for everyone.',zh:'挑選您的舞蹈形式和偏好的技能水平。我們為每個人準備了合適的課程。'} },
  { emoji:'🤝',step:'03',title:{en:'Meet Your Tutor',zh:'結識您的導師'},desc:{en:'Get matched to a certified dance expert who specialises in your chosen style.',zh:'為您配對一位專精於您所選風格的認證舞蹈專家。'} },
  { emoji:'📈',step:'04',title:{en:'Dance & Grow',zh:'舞蹈與成長'},desc:{en:'Track attendance, earn badges, hit objectives, and watch yourself improve daily.',zh:'追蹤出勤、贏取徽章、達成目標，見證自己每天的進步。'} },
];
const FAQS = [
  { q:{en:'Do I need any dance experience to join?',zh:'我需要有舞蹈經驗才能加入嗎？'},a:{en:"Absolutely not! We have Beginner batches for every dance style. Your tutor will assess your level and place you in the right batch.",zh:"絕對不需要！我們每個舞蹈風格都有初級班。您的導師會評估您的水平並將您安排在合適的班級。"} },
  { q:{en:'How do I login? Is it secure?',zh:'我該如何登入？安全嗎？'},a:{en:"Login securely using your registered email or mobile number and password. Safe authentication protects all parent and student records.",zh:"使用您註冊的電子郵件或手機號碼及密碼安全登入。安全認證保護所有家長和學生的記錄。"} },
  { q:{en:"Can parents track their child's progress?",zh:'家長可以追蹤孩子的進度嗎？'},a:{en:'Yes! Our Parent Portal gives guardians real-time visibility into attendance, tutor notes, objective progress, and fee status.',zh:'可以！我們的家長介面讓監護人能即時查看出勤、導師筆記、目標進度及學費狀態。'} },
  { q:{en:'What dance styles does ETS support?',zh:'ETS 支援哪些舞蹈風格？'},a:{en:'We currently support Hip Hop, Ballet, Salsa, Contemporary, Kathak, Breakdance, Jazz, Lyrical, and Tap Dance — with more being added regularly.',zh:'我們目前支援嘻哈、芭蕾、薩爾薩、當代舞、卡塔克舞、霹靂舞、爵士、抒情舞和踢踏舞 — 並且會定期新增更多風格。'} },
  { q:{en:'How does the Video Library work?',zh:'影片庫是如何運作的？'},a:{en:'The library has Morning Warm-ups (12 videos), Technique Masterclass (24 videos), and Dance Theory (8 documents). Pro members get full access.',zh:'影片庫提供晨間暖身（12 部影片）、技巧大師課（24 部影片）和舞蹈理論（8 份文檔）。Pro 會員可獲得完整存取權限。'} },
  { q:{en:'Can ETS manage my whole academy?',zh:'ETS 可以管理我整個學院嗎？'},a:{en:'Yes — the Academy plan covers unlimited students, multiple tutors, batch management, attendance, fee tracking, enquiry management, video library, and a full admin dashboard.',zh:'可以 — 學院方案涵蓋無限學生、多位導師、班級管理、出勤、學費追蹤、查詢管理、影片庫及完整的管理員儀表板。'} },
  { q:{en:'Can I cancel or change my plan anytime?',zh:'我可以隨時取消或更改我的方案嗎？'},a:{en:'Absolutely. Upgrade, downgrade, or cancel any time from your account settings. No lock-in contracts, no cancellation fees.',zh:'絕對可以。您可以隨時從帳戶設定升級、降級或取消。沒有綁定合約，沒有取消手續費。'} },
];
// TICKER and STATS are now dynamic — loaded from landingContent via API

function Stars({ n=5 }) { return <span style={{ color:'#c04060',letterSpacing:2 }}>{'★'.repeat(n)}</span>; }

function TestimonialCarousel({ lang = 'en' }) {
  const scrollRef = React.useRef(null);
  const scroll = (dir) => {
    if(scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 360, behavior: 'smooth' });
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      <style>{`
        .lp-carousel-track::-webkit-scrollbar { display: none; }
        .lp-carousel-track { -ms-overflow-style: none; scrollbar-width: none; }
        .lp-testi-card-wrapper { flex: 0 0 340px; scroll-snap-align: start; }
        @media (max-width: 768px) {
          .lp-testi-card-wrapper { flex: 0 0 85%; }
        }
      `}</style>

      {/* Scroll Track */}
      <div ref={scrollRef} className="lp-carousel-track" style={{ display: 'flex', overflowX: 'auto', gap: 24, padding: '10px 0 30px', scrollSnapType: 'x mandatory' }}>
        {TESTIMONIALS.map(testi => (
          <div key={testi.name} className="lp-testi-card-wrapper reveal">
            <div className="lp-testi-slide" style={{ height: '100%', margin: 0 }}>
              <div className="lp-testi-quote">"</div>
              <p className="lp-testi-text">{t(testi.quote, lang)}</p>
              <div className="lp-testi-stars"><Stars n={testi.stars}/></div>
              <div className="lp-testi-author">
                <div className="lp-testi-av">{testi.initial}</div>
                <div><div className="lp-testi-name">{testi.name}</div><div className="lp-testi-role">{t(testi.role, lang)}</div></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="lp-swiper-nav" style={{ marginTop: 24 }}>
        <button className="lp-swiper-btn lp-swiper-btn-light" onClick={() => scroll(-1)}>←</button>
        <button className="lp-swiper-btn lp-swiper-btn-light" onClick={() => scroll(1)}>→</button>
      </div>
    </div>
  );
}

const FALLBACK_TUTORS = [
  { _id: 'ft1', name: 'Ms. Clara Vance', specialization: ['Ballet', 'Lyrical'], experience: '8 Years', bio: 'Former soloist with European Ballet Company specializing in classical barre and grace.', rating: 4.9, studentCount: 42, photoUrl: 'https://images.pexels.com/photos/3757004/pexels-photo-3757004.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { _id: 'ft2', name: 'Marcus Jay', specialization: ['Hip Hop', 'Breakdance'], experience: '6 Years', bio: 'Champion street dancer focused on rhythm, isolation techniques, and stage confidence.', rating: 4.8, studentCount: 65, photoUrl: 'https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { _id: 'ft3', name: 'Elena Rostova', specialization: ['Contemporary', 'Jazz'], experience: '7 Years', bio: 'Passionate about expressive movement, flexibility training, and emotional storytelling.', rating: 5.0, studentCount: 38, photoUrl: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { _id: 'ft4', name: 'Carlos Mendoza', specialization: ['Salsa', 'Latin'], experience: '9 Years', bio: 'Bringing authentic Latin rhythm, footwork speed, and partner connection to every class.', rating: 4.9, studentCount: 54, photoUrl: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=600' }
];

function TutorCarousel({ publicTutors = [], lang = 'en' }) {
  const displayTutors = (publicTutors && publicTutors.length > 0) ? publicTutors : FALLBACK_TUTORS;
  const scrollRef = React.useRef(null);
  const scroll = (dir) => {
    if(scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      <style>{`
        .lp-tutor-card-wrapper { flex: 0 0 300px; scroll-snap-align: start; }
        @media (max-width: 768px) {
          .lp-tutor-card-wrapper { flex: 0 0 85%; }
        }
      `}</style>
      
      {/* Scroll Track */}
      <div ref={scrollRef} className="lp-carousel-track" style={{ display: 'flex', overflowX: 'auto', gap: 24, padding: '10px 0 30px', scrollSnapType: 'x mandatory' }}>
        {displayTutors.map(tutor=>{
          const tutorImg = resolveImage(tutor.photoUrl) || 'https://images.pexels.com/photos/2188012/pexels-photo-2188012.jpeg?auto=compress&cs=tinysrgb&w=600';
          const specialty = t(tutor.specialization, lang) || 'Dance Instructor';
          const ratingVal = tutor.rating || 4.5;
          const expNum = parseInt(tutor.experience, 10) || 0;
          const badgeText = ratingVal >= 4.8 ? 'Top Rated' : expNum >= 5 ? 'Senior Faculty' : 'Certified Faculty';
          const expText = tutor.experience ? (String(tutor.experience).toLowerCase().includes('year') ? tutor.experience : `${tutor.experience} Years`) : '3+ Years';
          
          return (
            <div key={tutor._id} className="lp-tutor-card-wrapper reveal">
              <div className="lp-tutor-card" style={{ height: '100%', margin: 0 }}>
                <div className="lp-tutor-img-wrap">
                  <img src={tutorImg} alt={t(tutor.name, lang)} className="lp-tutor-img"/>
                  <div className="lp-tutor-overlay">
                    <div className="lp-tutor-hover-content">
                      <p className="lp-tutor-bio">"{t(tutor.bio, lang) || 'Dedicated to inspiring and guiding dancers to achieve their personal artistic milestones.'}"</p>
                      <div className="lp-tutor-exp">💼 {expText} Experience</div>
                    </div>
                  </div>
                  <span className="lp-tutor-badge">{badgeText}</span>
                </div>
                <div className="lp-tutor-body">
                  <div className="lp-tutor-specialty">{specialty}</div>
                  <div className="lp-tutor-name">{t(tutor.name, lang)}</div>
                  <div className="lp-tutor-stars"><Stars n={Math.round(ratingVal)}/></div>
                  <div className="lp-tutor-stats-row">
                    <div className="lp-tutor-stat"><div className="num">{ratingVal}</div><div className="lbl">{t({en:'Rating',zh:'評分'}, lang)}</div></div>
                    <div className="lp-tutor-stat"><div className="num">{tutor.studentCount || 0}</div><div className="lbl">{t({en:'Students',zh:'學生'}, lang)}</div></div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="lp-swiper-nav" style={{ marginTop: 24 }}>
        <button className="lp-swiper-btn" onClick={() => scroll(-1)}>←</button>
        <button className="lp-swiper-btn" onClick={() => scroll(1)}>→</button>
      </div>
    </div>
  );
}

// Helper: safely read bilingual field {en, zh} or plain string
function t(field, lang = 'en') {
  if (!field) return '';

  if (typeof field === 'string') {
    // Plain string — return as-is (no translation object)
    const dict = {
      'Active Dancers': '活躍舞者', 'ACTIVE DANCERS': '活躍舞者',
      'Partner Academies': '合作學院', 'PARTNER ACADEMIES': '合作學院',
      'Expert Tutors': '專業導師', 'EXPERT TUTORS': '專業導師',
      'Dance Styles': '舞蹈種類', 'DANCE STYLES': '舞蹈種類',
      'Ballet': '芭蕾', 'Hip Hop': '嘻哈', 'Salsa': '騷莎', 'Breakdance': '霹靂舞',
      'Contemporary': '現代舞', 'Kpop': 'K-Pop', 'Latin': '拉丁舞',
      'Jazz': '爵士', 'Lyrical': '抒情舞', 'Kathak': '卡塔克舞',
      'Salsa & Latin': '薩爾薩與拉丁舞',
      'Location': '地點', 'WhatsApp': 'WhatsApp', 'Email': '電郵', 'Hours': '營業時間',
      'Rating': '評分', 'Students': '學生'
    };
    if (lang === 'zh') return dict[field] || field;
    return field;
  }

  // Bilingual object: prefer the requested language IF it has a non-empty value
  const zhVal = field.zh;
  const enVal = field.en;
  
  if (lang === 'zh') {
    // If it's an array (like specialization), check if it has items
    if (Array.isArray(zhVal)) return zhVal.length > 0 ? zhVal.join(' & ') : (Array.isArray(enVal) ? enVal.join(' & ') : '');
    // String
    return (zhVal && typeof zhVal === 'string' && zhVal.trim() !== '') ? zhVal : (enVal || '');
  }
  
  if (Array.isArray(enVal)) return enVal.join(' & ');
  return enVal || '';
}

function LangToggle({ lang, toggleLang, className = '' }) {
  return (
    <div 
      className={className}
      onClick={toggleLang}
      style={{
        display: 'flex',
        alignItems: 'center',
        background: '#f4f4f4',
        borderRadius: 30,
        padding: 4,
        position: 'relative',
        cursor: 'pointer',
        width: 90,
        height: 32,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
        userSelect: 'none'
      }}
      title={lang === 'en' ? 'Switch to Traditional Chinese' : 'Switch to English'}
    >
      <div 
        style={{
          position: 'absolute',
          top: 4,
          bottom: 4,
          left: 4,
          width: 41,
          background: '#fff',
          borderRadius: 20,
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          transform: lang === 'zh' ? 'translateX(41px)' : 'translateX(0)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)'
        }}
      />
      <div style={{ flex: 1, textAlign: 'center', zIndex: 1, fontSize: 11, fontWeight: 700, color: lang === 'en' ? '#111' : '#888', transition: 'color 0.3s' }}>
        EN
      </div>
      <div style={{ flex: 1, textAlign: 'center', zIndex: 1, fontSize: 11, fontWeight: 700, color: lang === 'zh' ? '#111' : '#888', transition: 'color 0.3s' }}>
        繁中
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [navScrolled, setNavScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [openFaq, setOpenFaq]         = useState(null);
  const [landingContent, setLandingContent] = useState(null);
  const [publicBatches, setPublicBatches] = useState([]);
  const [publicTutors, setPublicTutors] = useState([]);
  const [publicMerchandise, setPublicMerchandise] = useState([]);
  const [supportWhatsApp, setSupportWhatsApp] = useState('+852 5135 3966');
  const [lang, setLang] = useState(() => localStorage.getItem('ets_lang') || 'en');

  const toggleLang = () => {
    const next = lang === 'en' ? 'zh' : 'en';
    setLang(next);
    localStorage.setItem('ets_lang', next);
  };

  useEffect(() => {
    let isMounted = true;
    landingAPI.getContent().then(res => { if (isMounted && res.data) setLandingContent(res.data); }).catch(console.error);
    landingAPI.getPublicBatches().then(res => {
      if (!isMounted) return;
      const list = Array.isArray(res.data) ? res.data : (res.data?.batches || []);
      setPublicBatches(list);
    }).catch(console.error);
    landingAPI.getPublicTutors().then(res => {
      if (!isMounted) return;
      const list = Array.isArray(res.data) ? res.data : (res.data?.tutors || []);
      setPublicTutors(list);
    }).catch(console.error);
    landingAPI.getPublicMerchandise().then(res => {
      if (!isMounted) return;
      const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setPublicMerchandise(list);
    }).catch(console.error);
    landingAPI.getPublicSettings().then(res => {
      if (!isMounted) return;
      const wa = res.data?.supportWhatsApp;
      if (wa) setSupportWhatsApp(wa);
    }).catch(console.error);
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if(e.isIntersecting){e.target.classList.add('in');obs.unobserve(e.target);} });
    },{ threshold:0.1 });
    document.querySelectorAll('.reveal,.reveal-l,.reveal-r').forEach(el=>obs.observe(el));
    return ()=>obs.disconnect();
  },[publicTutors, publicBatches, landingContent]);

  useEffect(() => {
    const fn = () => {
      setNavScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = useCallback(id=>{ setMobileOpen(false); document.getElementById(id)?.scrollIntoView({behavior:'smooth',block:'start'}); },[]);

  const uniqueCategories = [];
  const seenCats = new Set();
  (publicBatches || []).forEach(b => {
    if (!b.category) return;
    const enVal = typeof b.category === 'object' ? b.category.en : b.category;
    if (!enVal) return;
    if (!seenCats.has(enVal)) {
      seenCats.add(enVal);
      uniqueCategories.push(b.category); // keep the original object so t() can translate it
    }
  });

  const displayCategories = uniqueCategories.length > 0 ? uniqueCategories : ['Ballet', 'Hip Hop', 'Salsa', 'Kathak'];
  const cleanWhatsApp = (supportWhatsApp || '').replace(/\D/g, '') || '85251353966';
  const categoryEmojiMap = {
    'Ballet': '🩰', 'Hip Hop': '👟', 'Salsa': '💃', 'Salsa & Latin': '💃', 'Breakdance': '🔥',
    'Contemporary': '✨', 'Kathak': '🎭', 'Jazz': '🎵', 'Lyrical': '🌊', 'Kpop': '🎤', 'Latin': '💃',
    'Freestyle': '🔥', 'Classical': '🎭', 'Classical Dance': '🎭'
  };
  const getEmoji = (cat) => {
    const catEn = typeof cat === 'object' ? (cat?.en || '') : (cat || '');
    const key = Object.keys(categoryEmojiMap).find(k => k.toLowerCase() === catEn.toLowerCase());
    return key ? categoryEmojiMap[key] : '✨';
  };

  return (
    <div style={{ fontFamily:"'Jost',sans-serif",background:'#ffffff',color:'#1a1a1a',overflowX:'hidden' }}>

      {/* TOPBAR */}
      <div className="lp-topbar">
        <span>🩰 {displayCategories.slice(0, 4).map(c => t(c, lang)).join(' · ')} — <strong>{t({en:'Enrol Now', zh:'立即報名'}, lang)}</strong></span>
        <span><a href={`https://wa.me/${cleanWhatsApp}`} style={{color:'inherit'}}>📞 {supportWhatsApp}</a></span>
      </div>

      {/* NAVBAR */}
      <nav className={`lp-nav ${navScrolled?'scrolled':''}`}>
        <div className="lp-nav-inner">
          <div onClick={()=>scrollTo('hero')} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
            <img src="/assets/logo.png" alt="ETS" style={{width:42,height:42,borderRadius:'50%',objectFit:'cover',border:'2px solid #111',filter:'grayscale(100%) contrast(1.1)'}}/>
            <div style={{lineHeight:1.1}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:18,color:'#111',letterSpacing:'-.3px'}}>ETS</div>
              <div style={{fontFamily:"'Jost',sans-serif",fontWeight:400,fontSize:9,color:'#9a9a9a',letterSpacing:'1.5px',textTransform:'uppercase'}}>{t({en:'Every Talent Shines',zh:'每個才華閃耀'}, lang)}</div>
            </div>
          </div>
          <ul className="lp-nav-links lp-nav-right-desktop">
            {[
              {id:'classes', name:{en:'Classes',zh:'課程'}},
              {id:'merchandise', name:{en:'Merchandise',zh:'學校產品'}},
              {id:'features', name:{en:'Features',zh:'特色'}},
              {id:'tutors', name:{en:'Tutors',zh:'導師'}},
              {id:'pricing', name:{en:'Pricing',zh:'定價'}},
              {id:'faq', name:{en:'FAQ',zh:'常見問題'}},
              {id:'contact', name:{en:'Contact',zh:'聯絡我們'}}
            ].map(link=>(
              <li key={link.id}><a href={`#${link.id}`} onClick={(e)=>{e.preventDefault(); scrollTo(link.id)}}>{t(link.name, lang)}</a></li>
            ))}
          </ul>
          <div className="lp-nav-right-desktop">
            <LangToggle lang={lang} toggleLang={toggleLang} />
            <button className="btn btn-sm btn-outline" onClick={()=>navigate('/login')}>{t({en:'Log In',zh:'登入'}, lang)}</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="lp-nav-mobile-only">
              <LangToggle lang={lang} toggleLang={toggleLang} />
            </div>
            <button className="lp-ham" onClick={()=>setMobileOpen(p=>!p)}>{mobileOpen?'✕':'☰'}</button>
          </div>
        </div>
      </nav>

      {/* MOBILE NAV */}
      <div className={`lp-mobile-nav ${mobileOpen?'open':''}`}>
        {[
          {id:'classes', name:{en:'Classes',zh:'課程'}},
          {id:'merchandise', name:{en:'Merchandise',zh:'學校產品'}},
          {id:'features', name:{en:'Features',zh:'特色'}},
          {id:'tutors', name:{en:'Tutors',zh:'導師'}},
          {id:'pricing', name:{en:'Pricing',zh:'定價'}},
          {id:'faq', name:{en:'FAQ',zh:'常見問題'}},
          {id:'contact', name:{en:'Contact',zh:'聯絡我們'}}
        ].map(link=>(
          <a key={link.id} href={`#${link.id}`} onClick={(e)=>{e.preventDefault(); scrollTo(link.id)}}>{t(link.name, lang)}</a>
        ))}
        <button className="btn btn-outline" style={{marginTop:24,width:'100%',justifyContent:'center'}} onClick={()=>navigate('/login')}>
          {t({en:'Log In',zh:'登入'}, lang)}
        </button>
      </div> 

      {/* ═══════════ HERO — SPLIT LAYOUT ═══════════ */}
      <Hero content={landingContent} scrollTo={scrollTo} lang={lang} />
      {/* TICKER — dynamic dance styles from backend */}
      <div className="lp-ticker">
        <div className="lp-ticker-track">
          {(() => {
            const styles = displayCategories.map(cat => ({ emoji: getEmoji(cat), name: cat }));
            // Duplicate for seamless loop (4x to ensure enough width for small lists)
            return [...styles, ...styles, ...styles, ...styles].map((s, i) => (
              <span key={i} className="lp-ticker-item">{s.emoji} {t(tField(s.name), lang)}</span>
            ));
          })()}
        </div>
      </div>

      {/* STATS — dynamic counts from backend */}
      <div style={{background:'#f7f7f7'}}>
        <div className="lp-stats-row" style={{maxWidth:'100%',borderTop:'1px solid #e0e0e0',borderLeft:'none'}}>
          {(landingContent?.stats?.length
            ? landingContent.stats
            : [
                { number: '12,000+', label: { en: 'Active Dancers',    zh: '活躍舞者' } },
                { number: '150+',    label: { en: 'Partner Academies', zh: '合作學院' } },
                { number: '45+',     label: { en: 'Expert Tutors',     zh: '專業導師' } },
                { number: '9',       label: { en: 'Dance Styles',      zh: '舞蹈種類' } }
              ]
          ).map((stat, idx) => (
            <div key={idx} className="lp-stat-item">
              <div className="lp-stat-num">{stat.number}</div>
              <div className="lp-stat-label">{t(stat.label, lang)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ABOUT */}
      <About content={landingContent} scrollTo={scrollTo} lang={lang} />

      {/* CLASSES */}
      <Classes publicBatches={publicBatches} scrollTo={scrollTo} lang={lang} />

      {/* SCHOOL PRODUCTS / MERCHANDISE */}
      <MerchandiseSection publicMerchandise={publicMerchandise} whatsappNumber={supportWhatsApp} lang={lang} />

      {/* HOW IT WORKS */}
      <section className="lp-sec lp-sec-dark" id="process">
        <div className="lp-container">
          <div className="reveal" style={{textAlign:'center'}}>
            <div className="lp-overline lp-overline-dim">{t({en:'Simple Process',zh:'簡單流程'}, lang)}</div>
            <h2 className="lp-sec-title" style={{color:'#fff'}}>{t({en:'Dancing Starts in ',zh:'舞蹈始於 '}, lang)}<em className="lp-text-gold">{t({en:'4 Steps',zh:'4 個步驟'}, lang)}</em></h2>
          </div>
          <div className="lp-steps-grid reveal">
            {STEPS.map(s=>(
              <div key={s.title.en} className="lp-step-card">
                <div className="lp-step-num">{s.step}</div>
                <div className="lp-step-icon-wrap">{s.emoji}</div>
                <div className="lp-step-title">{t(s.title, lang)}</div>
                <p className="lp-step-desc">{t(s.desc, lang)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="lp-sec" id="features">
        <div className="lp-container">
          <div className="reveal" style={{textAlign:'center'}}>
            <div className="lp-overline">{t({en:'Platform Features',zh:'平台特色'}, lang)}</div>
            <h2 className="lp-sec-title">{t({en:'Everything You Need to ',zh:'您需要的一切盡在 '}, lang)}<em className="lp-text-gold">{t({en:'Excel',zh:'掌握'}, lang)}</em></h2>
          </div>
          <div className="lp-features-grid reveal">
            {FEATURES.map(f=>(
              <div key={f.title.en} className="lp-feat-card">
                <span className="lp-feat-icon">{f.emoji}</span>
                <div className="lp-feat-title">{t(f.title, lang)}</div>
                <p className="lp-feat-desc">{t(f.desc, lang)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TUTORS */}
      <section className="lp-sec lp-sec-alt" id="tutors">
        <div className="lp-container">
          <div className="reveal" style={{textAlign:'center'}}>
            <div className="lp-overline">{t({en:'Meet the Experts',zh:'結識專家'}, lang)}</div>
            <h2 className="lp-sec-title">{t({en:'Your World-Class ',zh:'您的世界級 '}, lang)}<em className="lp-text-gold">{t({en:'Instructors',zh:'導師'}, lang)}</em></h2>
          </div>
          <TutorCarousel publicTutors={publicTutors} lang={lang} />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="lp-sec lp-sec-dark2" id="testimonials">
        <div className="lp-container">
          <div className="reveal" style={{textAlign:'center',marginBottom:52}}>
            <div className="lp-overline lp-overline-dim">{t({en:'Real Stories',zh:'真實故事'}, lang)}</div>
            <h2 className="lp-sec-title" style={{color:'#fff'}}>{t({en:'Dancers Love ',zh:'舞者熱愛 '}, lang)}<em className="lp-text-gold">{t({en:'ETS Academy',zh:'ETS 舞蹈學院'}, lang)}</em></h2>
          </div>
          <TestimonialCarousel lang={lang} />
        </div>
      </section>

      {/* PRICING */}
      <Pricing publicBatches={publicBatches} scrollTo={scrollTo} lang={lang} />

      {/* FAQ */}
      <section className="lp-sec lp-sec-alt" id="faq">
        <div className="lp-container" style={{maxWidth:820}}>
          <div className="reveal" style={{textAlign:'center'}}>
            <div className="lp-overline">{t({en:'Got Questions?',zh:'有疑問嗎？'}, lang)}</div>
            <h2 className="lp-sec-title">{t({en:'Frequently ',zh:'常見 '}, lang)}<em className="lp-text-gold">{t({en:'Asked',zh:'問題'}, lang)}</em></h2>
          </div>
          <div style={{marginTop:52}}>
            {FAQS.map((faq,i)=>(
              <div key={i} className={`lp-faq-item ${openFaq===i?'open':''}`}>
                <button className={`lp-faq-btn ${openFaq===i?'open':''}`} onClick={()=>setOpenFaq(openFaq===i?null:i)}>
                  <span>{t(faq.q, lang)}</span><div className="lp-faq-icon">+</div>
                </button>
                <div className="lp-faq-answer">{t(faq.a, lang)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="lp-sec" id="contact">
        <div className="lp-container">
          <div className="reveal" style={{textAlign:'center',marginBottom:64}}>
            <div className="lp-overline">{t({en:'Get In Touch',zh:'與我們聯繫'}, lang)}</div>
            <h2 className="lp-sec-title">{t({en:'Ready to ',zh:'準備好 '}, lang)}<em className="lp-text-gold">{t({en:'Start?',zh:'開始了嗎？'}, lang)}</em></h2>
            <p className="lp-sec-sub" style={{margin:'0 auto'}}>{t({en:"Drop us your details and we'll reach out within 24 hours.",zh:'留下您的聯絡方式，我們將在 24 小時內與您聯繫。'}, lang)}</p>
          </div>
          <div className="lp-contact-grid reveal">
            <div>
              <div className="lp-overline">{t(landingContent?.contactOverline, lang)}</div>
              <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:28,marginBottom:28}}>{t(landingContent?.contactTitleRegular, lang)}<em style={{fontStyle:'italic',color:'#7b1d3a'}}>{t(landingContent?.contactTitleItalic, lang)}</em></h3>
              {[
                ['📍','Location', t(landingContent?.contactLocation, lang)],
                ['📞','WhatsApp', t(landingContent?.contactPhone, lang)],
                ['✉️','Email', t(landingContent?.contactEmail, lang)],
                ['🕐','Hours', t(landingContent?.contactHours, lang)]
              ].map(([icon,lbl,val])=>(
                <div key={lbl} className="lp-contact-info-item">
                  <div className="lp-contact-icon">{icon}</div>
                  <div><div className="lp-contact-label">{t(lbl, lang)}</div><div className="lp-contact-val">{val}</div></div>
                </div>
              ))}
              <div style={{display:'flex',gap:10,marginTop:20,flexWrap:'wrap'}}>
                <a href="https://www.facebook.com/profile.php?id=61578465183527" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline" style={{gap:6}}>📘 Facebook</a>
                <a href="https://www.instagram.com/ets_acadermy" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline" style={{gap:6}}>📸 Instagram</a>
                <a href={`https://wa.me/${cleanWhatsApp}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary" style={{gap:6}}>💬 WhatsApp</a>
              </div>
              <img src="/assets/ballet.jpg" alt="Studio" style={{width:'100%',height:200,objectFit:'cover',marginTop:24,filter:'grayscale(20%)'}}/>
            </div>
            <div>
              <EnquiryForm lang={lang} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA content={landingContent} scrollTo={scrollTo} lang={lang} />

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-grid">  
          <div>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
              <img src="/assets/logo.png" alt="ETS" style={{width:52,height:52,borderRadius:'50%',objectFit:'cover',border:'2px solid white',filter:'grayscale(100%) brightness(1.15)',background:'white'}}/>
              <div>
                <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:22,color:'#fff',letterSpacing:'-.3px',lineHeight:1}}>ETS</div>
                <div style={{fontSize:10,color:'rgba(255,255,255,.35)',letterSpacing:'1.5px',textTransform:'uppercase',marginTop:3}}>{t({en:'Every Talent Shines Academy',zh:'Every Talent Shines 舞蹈學院'}, lang)}</div>
              </div>
            </div>
            <p className="lp-footer-tagline">{t(landingContent?.footerTagline, lang)}</p>
            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(255,255,255,.7)', lineHeight: 1.6 }}>
              <strong style={{ color: '#fff' }}>ETS (a.k.a Every Talent Shine Academy)</strong><br />
              UNIT20A, 5/F SING WIN FACTORY BUILDING<br />
              15-17 SHING YIP STREET KWUN TING,<br />
              KOWLOON HONG KONG<br />
              Represented By: Mrs. Esther Lam
            </div>
            <div style={{marginTop:20}}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:'rgba(255,255,255,.25)',marginBottom:14}}>{t({en:'Connect With Us',zh:'與我們聯繫'}, lang)}</div>
              <div style={{display:'flex',flexWrap:'nowrap',gap:12,maxWidth:'100%',alignItems:'center',overflowX:'auto'}}>
                {[
                  {src:'/assets/qr-facebook.png',href:'https://www.facebook.com/profile.php?id=61578465183527',label:'Facebook'},
                  {src:'/assets/qr-instagram.png',href:'https://www.instagram.com/ets_acadermy',label:'Instagram'},
                  {src:'/assets/qr-whatsapp.png',href:`https://wa.me/${cleanWhatsApp}`,label:'WhatsApp'},
                  {src:'/assets/qr-xhslink.png',href:'https://xhslink.com/m/46ANhYP4sWe',label:'XHS Link'},
                ].map(qr=>(
                  <a key={qr.label} href={qr.href} target="_blank" rel="noopener noreferrer" style={{textAlign:'center',textDecoration:'none',minWidth:110}}>
                    <img src={qr.src} alt={qr.label} style={{width:'100%',maxWidth:110,height:'auto',border:'1px solid rgba(255,255,255,.1)',display:'block',margin:'0 auto 5px'}}/>
                    <div style={{fontSize:11,color:'rgba(255,255,255,.4)',display:'flex',justifyContent:'center',alignItems:'center',gap:4}}>
                      {qr.label === 'Facebook' && <span style={{color:'#1877f2',fontWeight:700}}>F</span>}
                      {qr.label === 'Instagram' && <span style={{color:'#C13584',fontWeight:700}}>I</span>}
                      {qr.label === 'WhatsApp' && <span style={{color:'#25D366',fontWeight:700}}>W</span>}
                      {qr.label === 'XHS Link' && <span style={{color:'#ff7f00',fontWeight:700}}>X</span>}
                      <span>{qr.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
          {[
            {head:{en:'Platform',zh:'平台'},links:[{en:'Dance Classes',zh:'舞蹈課程'},{en:'Features',zh:'特色'},{en:'Find a Tutor',zh:'尋找導師'},{en:'Pricing',zh:'定價'}]},
            {head:{en:'Dance Styles',zh:'舞蹈種類'},links:[{en:'Hip Hop',zh:'嘻哈'},{en:'Ballet',zh:'芭蕾'},{en:'Salsa & Latin',zh:'薩爾薩與拉丁舞'},{en:'Contemporary',zh:'現代舞'},{en:'Kathak',zh:'卡塔克舞'}]},
            {head:{en:'For Academies',zh:'給舞蹈學院'},links:[{en:'Admin Dashboard',zh:'管理員儀表板'},{en:'Tutor Portal',zh:'導師門戶'},{en:'Batch Management',zh:'班級管理'},{en:'Fee Tracking',zh:'學費追蹤'}]},
          ].map(col=>(
            <div key={t(col.head, lang)} className="lp-footer-col">
              <div className="lp-footer-col-head">{t(col.head, lang)}</div>
              {col.links.map(l=><a key={t(l, 'en')} href="#!" onClick={(e)=>e.preventDefault()}>{t(l, lang)}</a>)}
            </div>
          ))}
        </div>
        <div className="lp-footer-bottom">
          <span>{t({en:'© 2023 Ilytics Technology Private Limited. All rights reserved.',zh:'© 2023 Ilytics Technology Private Limited. 保留所有權利。'}, lang)}</span>
          <div className="lp-footer-legal">
            <a href="/privacy-policy" onClick={(e)=>{ e.preventDefault(); navigate('/privacy-policy'); }}>{t({en:'Privacy Policy',zh:'隱私權政策'}, lang)}</a>
            <a href="/terms-of-service" onClick={(e)=>{ e.preventDefault(); navigate('/terms-of-service'); }}>{t({en:'Terms of Service',zh:'服務條款'}, lang)}</a>
            <a href="/cookie-policy" onClick={(e)=>{ e.preventDefault(); navigate('/cookie-policy'); }}>{t({en:'Cookie Policy',zh:'Cookie 政策'}, lang)}</a>
            <a href="/delete-account" onClick={(e)=>{ e.preventDefault(); navigate('/delete-account'); }}>{t({en:'Delete Account',zh:'刪除帳號'}, lang)}</a>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button 
          className="lp-scroll-top-btn" 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
}
